import { IcDroneFace, IcFlightPlan, IcFocus, IcTrackingDrone } from '@assets/icons/32x32';
import { For, HStack, Icon, Tabs, Text, VStack } from '@chakra-ui/react';
import { useMapContext } from '@components/map/MapContext';
import { DraggablePanel } from '@components/ui/draggable';
import { Tooltip } from '@components/ui/tooltip';
import { useAppSelector } from '@hooks/redux.hooks';
import { useSelectorWithReplayMode } from '@hooks/useSelectorWithReplayMode';
import { hasRole, selectActiveOrganization } from '@redux/authent/authent.selectors';
import { makeSelectPlatformsNamesByCode } from '@redux/config/config.selectors';
import { selectNotificationsByIdentifier } from '@redux/data/data.selectors';
import { selectIsEngagedTargetById, selectTargetById } from '@redux/situation/situation.selectors';
import { RootState } from '@redux/store';
import { skipToken } from '@reduxjs/toolkit/query';
import { useGetDroneByCodeQuery } from '@services/utm/drone.api';
import { getPlatformsCodesFromMarks } from '@utils/common.utils';
import { getTargetIcon, getTargetIdentification, isDrone } from '@utils/target.utils';
import { capitalize } from 'lodash';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { shallowEqual } from 'react-redux';

import { RoleEnum } from '@/types/authent/roles.types';
import { Target } from '@/types/c2/c2.types';
import { ActionEnum } from '@/types/commons/commons.types';
import { DroneNotificationLight } from '@/types/data/data.types';
import { MapIdEnum } from '@/types/map.types';
import { PopupPositionData, TargetInfoData } from '@/types/popup.types';
import { TargetLocation } from '@/types/sensor/sensor.types';

import TargetActions from './actions/TargetActions';
import TargetDrone from './drone/TargetDrone';
import TargetInformation from './information/TargetInformation';
import TargetFlightPlans from './TargetFlightPlans';
import TargetId from './TargetId';

export const SEE_NOTIFICATIONS_ROLE = [ActionEnum.ACCESS_TARGET];

export default function TargetInfo({
  mapConstraintsRef,
  targetId,
  top,
  left,
  onClose,
}: Readonly<TargetInfoData & PopupPositionData>) {
  const { formatMessage } = useIntl();
  const { mapId } = useMapContext();
  const isReplayMode = mapId === MapIdEnum.REPLAY;
  const target: Target<TargetLocation> | undefined = useSelectorWithReplayMode(selectTargetById, targetId);
  const isEngaged = useSelectorWithReplayMode(selectIsEngagedTargetById, targetId);
  const organization = useAppSelector(selectActiveOrganization);
  const canAccessFlightPlan = useAppSelector((state: RootState) => hasRole(state, RoleEnum.ACCESS_FLIGHT_PLAN));
  const canAccessDrone = useAppSelector((state: RootState) => hasRole(state, RoleEnum.ACCESS_DRONE));
  const canSeePlatform = useAppSelector((state: RootState) => hasRole(state, RoleEnum.ACCESS_PLATFORM));
  const { data: drone } = useGetDroneByCodeQuery(
    target?.droneCode && canAccessDrone ? { code: target.droneCode, site: target.site } : skipToken,
  );
  const platformCodes = useMemo(() => getPlatformsCodesFromMarks(target?.marks ?? []), [target?.marks]);
  const selectPlatformNames = useMemo(makeSelectPlatformsNamesByCode, []);
  const platforms = useAppSelector((state) => selectPlatformNames(state, platformCodes), shallowEqual);
  const notification =
    useAppSelector((state) => selectNotificationsByIdentifier(state, target?.id)).find(
      (notification) => notification.endTime === null,
    ) ?? null;

  if (!target) {
    onClose();
    return null;
  }

  const showDroneTabs = isDrone(target, organization) && target.displayId !== null;
  const model = target.collaborativeData?.model;
  const modelLabel = model === undefined ? '' : ` ${model}`;
  const droneName = drone?.functionalName ?? target.displayId;

  return (
    <DraggablePanel.Root
      key={target.id}
      constraintsRef={mapConstraintsRef}
      top={top}
      left={left}
      offsetY={-100}
      offsetX={50}
    >
      <DraggablePanel.Header onClose={onClose}>
        {formatMessage(
          { id: 'targetInfos.targetInfo' },
          {
            identification: capitalize(
              formatMessage({ id: `target.identification.${getTargetIdentification(target, organization)}` }),
            ),
          },
        )}
      </DraggablePanel.Header>
      <DraggablePanel.Body width={{ base: '452px', '4k': '520px' }}>
        <VStack gap={0.25} width="100%" height="100%" flex={1} backgroundColor="neutral.900">
          <HStack
            width="100%"
            height="120px"
            gap="standardS"
            paddingX="standardM"
            backgroundColor="neutral.700"
            justifyContent="center"
          >
            <Icon as={getTargetIcon(target, isEngaged, organization)} width="70px" height="70px" />
            <VStack gap={0.5} alignItems="start" width="100%">
              <TargetId
                label={droneName ? `${droneName}${modelLabel}` : formatMessage({ id: 'target.unknown' })}
                textStyle="screenStd"
              />
              {canSeePlatform && (
                <HStack maxWidth="100%" gap={0.5} flexWrap="wrap">
                  <For each={platforms}>
                    {(platform) => (
                      <Tooltip key={platform} content={platform} openDelay={200}>
                        <Text
                          paddingX={0.25}
                          paddingY={0.5}
                          textStyle="screenStd"
                          backgroundColor="neutral.600"
                          textTransform="uppercase"
                          fontWeight="medium"
                          cursor="default"
                          color="neutral.200"
                          key={platform}
                        >
                          {platform.slice(0, 4)}
                        </Text>
                      </Tooltip>
                    )}
                  </For>
                </HStack>
              )}
            </VStack>
          </HStack>
          <Tabs.Root
            width="100%"
            size="screenStd"
            variant="neutral"
            height="max-content"
            lazyMount
            defaultValue="information"
          >
            <Tabs.List>
              <Tooltip content={formatMessage({ id: 'targetInfos.tab.information.title' })}>
                <Tabs.Trigger value="information">
                  <Icon as={IcFocus} boxSize={{ base: '24px', '4k': '32px' }} />
                </Tabs.Trigger>
              </Tooltip>
              <Tooltip content={formatMessage({ id: 'targetInfos.tab.actions.title' })}>
                <Tabs.Trigger value="actions">
                  <Icon as={IcTrackingDrone} boxSize={{ base: '24px', '4k': '32px' }} />
                </Tabs.Trigger>
              </Tooltip>
              {showDroneTabs && (
                <>
                  {canAccessDrone && !isReplayMode && (
                    <Tooltip content={formatMessage({ id: 'targetInfos.tab.drone.title' })}>
                      <Tabs.Trigger value="drone">
                        <Icon as={IcDroneFace} boxSize={{ base: '24px', '4k': '32px' }} />
                      </Tabs.Trigger>
                    </Tooltip>
                  )}
                  {canAccessFlightPlan && (
                    <Tooltip content={formatMessage({ id: 'targetInfos.tab.flightPlans.title' })}>
                      <Tabs.Trigger value="flightPlans">
                        <Icon as={IcFlightPlan} boxSize={{ base: '24px', '4k': '32px' }} />
                      </Tabs.Trigger>
                    </Tooltip>
                  )}
                </>
              )}
            </Tabs.List>
            <Tabs.ContentGroup>
              <Tabs.Content value="information">
                <TargetInformation
                  target={target}
                  notification={notification ? (notification as DroneNotificationLight) : undefined}
                  position={{ x: left, y: top }}
                />
              </Tabs.Content>
              <Tabs.Content value="actions">
                <TargetActions target={target} position={{ x: left, y: top }} />
              </Tabs.Content>
              {showDroneTabs && (
                <>
                  {canAccessDrone && !isReplayMode && (
                    <Tabs.Content value="drone">
                      <TargetDrone target={target} />
                    </Tabs.Content>
                  )}
                  {canAccessFlightPlan && (
                    <Tabs.Content value="flightPlans">
                      <TargetFlightPlans
                        flightPlanCodes={target.fpCodes}
                        targetId={target.id}
                        droneCode={target.droneCode}
                        position={{ x: left, y: top }}
                      />
                    </Tabs.Content>
                  )}
                </>
              )}
            </Tabs.ContentGroup>
          </Tabs.Root>
        </VStack>
      </DraggablePanel.Body>
    </DraggablePanel.Root>
  );
}
